import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Scheduler from 'devextreme-react/scheduler';
import { fetchWorkcellActivity, fetchDailyHoursSummary } from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import 'devextreme/dist/css/dx.light.css';
import DailyViewLayout from '../components/DailyViewLayout';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatLocalDate, formatSchedulerDate, formatSchedulerTime, parseLocalDateTime, parseSchedulerDateTime } from '../utils/localDateTime';

const getActivityColor = (percentage) => {
    // Below 50% is pure red
    if (percentage <= 50) {
        return 'rgb(255, 0, 0)';
    }
    // Above 95% is pure green
    if (percentage >= 95) {
        return 'rgb(0, 255, 0)';
    }

    // Two-phase transition: red -> orange -> green
    const factor = (percentage - 50) / 45; // 0 at 50%, 1 at 95%

    if (factor < 0.5) {
        // First half: red to orange (255,0,0) -> (255,165,0)
        const orangeFactor = factor * 2; // rescale 0-0.5 to 0-1
        return `rgb(255, ${Math.round(165 * orangeFactor)}, 0)`;
    } else {
        // Second half: orange to green (255,165,0) -> (0,255,0)
        const greenFactor = (factor - 0.5) * 2; // rescale 0.5-1 to 0-1
        return `rgb(${Math.round(255 * (1 - greenFactor))}, ${Math.round(165 + (255 - 165) * greenFactor)}, 0)`;
    }
};

const getDateString = date => format(date, 'yyyy-MM-dd');

const transformActivityData = (activities, summaryMap) => {
    // Filter only OEE-enabled workcells
    const filtered = activities.filter(a => a.has_oee === true || a.has_oee === 1);

    return filtered.map((activity, index) => {
        const activityDate = formatLocalDate(activity.from_time);
        const key = `${activity.workcell_id}_${activityDate}`;
        const percentage = summaryMap[key] || 0;
        return {
            id: index,
            text: activity.workcell_name,
            startDate: parseSchedulerDateTime(activity.from_time),
            endDate: parseSchedulerDateTime(activity.to_time),
            workcellId: activity.workcell_id,
            timeslotIds: Array.isArray(activity.timeslot_ids) ? activity.timeslot_ids : [],
            color: getActivityColor(percentage)
        };
    });
};

const DailyWorkcellSchedulePage = () => {
    const [activities, setActivities] = useState([]);
    const [currentView, setCurrentView] = useState('week');
    const navigate = useNavigate();
    const { date } = useParams();
    const selectedDate = date || formatLocalDate(new Date());
    const { t } = useTranslation();

    const formatTime = (dt) => {
        if (!dt) return t('common.na');
        return formatSchedulerTime(dt) || t('common.na');
    };

    const handleAppointmentFormOpening = (e) => {
        const timeslotIdsText = Array.isArray(e.appointmentData.timeslotIds) && e.appointmentData.timeslotIds.length > 0
            ? e.appointmentData.timeslotIds.join(', ')
            : t('common.na');

        const formData = {
            ...e.appointmentData,
            timeslotIdsText,
        };

        e.form.option('formData', formData);

        const items = [...(e.form.option('items') || [])];
        const timeslotItem = {
            dataField: 'timeslotIdsText',
            editorType: 'dxTextArea',
            colSpan: 2,
            label: { text: t('daily.workcellSchedule.tooltip.timeslotIds') },
            editorOptions: {
                readOnly: true,
                autoResizeEnabled: true,
                minHeight: 80,
                maxHeight: 180,
            },
        };

        const mainGroup = items.find((item) => item.name === 'mainGroup');
        if (mainGroup && Array.isArray(mainGroup.items)) {
            mainGroup.items = [
                ...mainGroup.items.filter((item) => item.dataField !== 'timeslotIdsText'),
                timeslotItem,
            ];
        } else {
            items.push(timeslotItem);
        }

        e.form.option('items', items);
    };

    useEffect(() => {
        console.log('currentView:', currentView, 'selectedDate:', selectedDate);
        if (!date || date !== selectedDate) {
            navigate(`/chart/daily/workcell-schedule/${selectedDate}`);
            return;
        }

        const loadData = async () => {
            try {
                let activityData, summaryMap = {};
                if (currentView === 'week') {
                    const weekStart = startOfWeek(parseLocalDateTime(`${selectedDate}T00:00:00`), { weekStartsOn: 1 });
                    const weekEnd = endOfWeek(parseLocalDateTime(`${selectedDate}T00:00:00`), { weekStartsOn: 1 });
                    const weekDates = [];
                    for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
                        weekDates.push(getDateString(new Date(d)));
                    }
                    activityData = await fetchWorkcellActivity(undefined, getDateString(weekStart), getDateString(weekEnd));
                    // Fetch all summaries in parallel, and pair with their date
                    const summaries = await Promise.all(
                        weekDates.map(day => fetchDailyHoursSummary(day).then(data => ({ day, data })))
                    );
                    // Flatten and map by workcell_id + correct date
                    summaries.forEach(({ day, data }) => {
                        (data || []).forEach(summary => {
                            const key = `${summary.workcell_id}_${day}`;
                            summaryMap[key] = summary.activity_percentage;
                        });
                    });
                } else {
                    activityData = await fetchWorkcellActivity(selectedDate);
                    const summaryData = await fetchDailyHoursSummary(selectedDate);
                    summaryData.forEach(summary => {
                        const key = `${summary.workcell_id}_${selectedDate}`;
                        summaryMap[key] = summary.activity_percentage;
                    });
                }
                const transformedData = transformActivityData(activityData, summaryMap);
                setActivities(transformedData);
            } catch (error) {
                console.error('Error loading workcell data:', error);
            }
        };

        loadData();
    // eslint-disable-next-line
    }, [date, selectedDate, navigate, currentView]);

    const schedulerProps = {
        dataSource: activities,
        views: ['day', 'week', 'month', 'timelineDay'],
        currentView,
        onCurrentViewChange: setCurrentView,
        currentDate: parseSchedulerDateTime(`${selectedDate}T00:00:00`),
        timeZone: 'UTC',
        width: '100%',
        groups: ['workcellId'],
        // cellDuration: 360,
        startDayHour: 6,
        endDayHour: 20,
        resourceCellRender: (model) => model.data.text,
        onCurrentDateChange: (newDate) => {
            const formattedDate = formatSchedulerDate(newDate);
            navigate(`/chart/daily/workcell-schedule/${formattedDate}`);
        },
        editing: {
            allowAdding: false,
            allowUpdating: false,
            allowDeleting: false
        },
        onAppointmentFormOpening: handleAppointmentFormOpening,
        resources: [{
            fieldExpr: 'workcellId',
            dataSource: Object.values(activities.reduce((acc, curr) => {
                if (!acc[curr.workcellId]) {
                    acc[curr.workcellId] = { id: curr.workcellId, text: curr.text };
                }
                return acc;
            }, {}))
        }],
        appointmentRender: (model) => (
            <div style={{
                backgroundColor: model.appointmentData.color,
                height: '100%',
                width: '100%',
                border: 'none',
                borderRadius: 0,
                margin: 0
            }} />
        ),
        appointmentTooltipRender: (model) => {
            console.log('Tooltip Data:', model.appointmentData);
            return (
                <div>
                    <strong>{model.appointmentData.text}</strong>
                    <div>
                        {t('daily.workcellSchedule.tooltip.duration')}: {formatTime(model.appointmentData.startDate)} — {formatTime(model.appointmentData.endDate)}
                    </div>
                    <div>{t('daily.workcellSchedule.tooltip.parts')}: {model.appointmentData.text}</div>
                </div>
            );
        },
        onAppointmentRendered: (e) => {
            // Remove the blue background from parent elements
            const parentElements = e.appointmentElement.parentElement;
            if (parentElements) {
                parentElements.style.backgroundColor = 'transparent';
                parentElements.style.border = 'none';
            }
        }
    };

    return (
        <DailyViewLayout date={selectedDate}>
            <Box sx={{
                '& .dx-scheduler-appointment': {
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    borderRadius: '0 !important',
                    padding: '0 !important'
                },
                '& .dx-scheduler-appointment-content': {
                    padding: '0 !important'
                }
            }}>
                <Scheduler {...schedulerProps} />
            </Box>
        </DailyViewLayout>
    );
};

export default DailyWorkcellSchedulePage;
